import { useState, useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { NativeBridge } from '../native/NativeBridge';
import { KeepAliveBridge } from '../native/KeepAliveBridge';

/** All permissions the app requires to function properly */
interface PermissionStatus {
  microphone: PermState;
  camera: PermState;
  location: PermState;
  notifications: PermState;
}

type PermState = 'granted' | 'denied' | 'prompt' | 'unknown';

const isNative = Capacitor.isNativePlatform();

// ── Web fallback (used when running in browser, not native) ──

async function checkPermissionWeb(name: string): Promise<PermState> {
  try {
    const result = await navigator.permissions.query({ name } as any);
    return result.state as PermState;
  } catch {
    return 'unknown';
  }
}

async function checkAllWeb(): Promise<PermissionStatus> {
  const [microphone, camera, location] = await Promise.all([
    checkPermissionWeb('microphone'),
    checkPermissionWeb('camera'),
    checkPermissionWeb('geolocation'),
  ]);
  let notifications: PermState = 'unknown';
  if (typeof Notification !== 'undefined') {
    notifications = Notification.permission === 'granted' ? 'granted'
      : Notification.permission === 'denied' ? 'denied' : 'prompt';
  }
  return { microphone, camera, location, notifications };
}

async function requestAllWeb(): Promise<PermissionStatus> {
  const results: PermissionStatus = {
    microphone: 'denied',
    camera: 'denied',
    location: 'denied',
    notifications: 'denied',
  };
  try {
    const s = await navigator.mediaDevices.getUserMedia({ audio: true });
    s.getTracks().forEach(t => t.stop());
    results.microphone = 'granted';
  } catch { /* denied */ }
  try {
    const s = await navigator.mediaDevices.getUserMedia({ video: true });
    s.getTracks().forEach(t => t.stop());
    results.camera = 'granted';
  } catch { /* denied */ }
  try {
    await new Promise<void>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(() => resolve(), () => reject(), { timeout: 10000 });
    });
    results.location = 'granted';
  } catch { /* denied */ }
  if (typeof Notification !== 'undefined') {
    const r = await Notification.requestPermission();
    results.notifications = r === 'granted' ? 'granted' : 'denied';
  }
  return results;
}

// ── Hook ─────────────────────────────────────────────────────

export function usePermissions() {
  const [permissions, setPermissions] = useState<PermissionStatus>({
    microphone: 'unknown',
    camera: 'unknown',
    location: 'unknown',
    notifications: 'unknown',
  });
  const [checking, setChecking] = useState(true);
  // Track whether we've attempted a request — if a permission is still
  // not granted after a request attempt, it's permanently denied.
  const hasRequestedRef = useRef(false);

  const refresh = useCallback(async () => {
    if (isNative) {
      // Use Android's native ContextCompat.checkSelfPermission()
      const result = await KeepAliveBridge.checkPermissions();
      if (result) {
        setPermissions({
          microphone: result.microphone as PermState,
          camera: result.camera as PermState,
          location: result.location as PermState,
          notifications: result.notifications as PermState,
        });
      }
    } else {
      const status = await checkAllWeb();
      setPermissions(status);
    }
    setChecking(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Re-check permissions when app resumes from background.
  useEffect(() => {
    const handle = NativeBridge.onAppStateChange((isActive: boolean) => {
      if (isActive) {
        console.log('[Permissions] App resumed, re-checking permissions...');
        refresh();
      }
    });
    return () => {
      Promise.resolve(handle).then((h) => h?.remove?.());
    };
  }, [refresh]);

  const requestAll = useCallback(async () => {
    hasRequestedRef.current = true;

    if (isNative) {
      // Use Android's native requestPermissions dialog.
      // Capacitor handles showing the system prompt and returning results.
      const result = await KeepAliveBridge.requestPermissions();
      if (result) {
        setPermissions({
          microphone: result.microphone as PermState,
          camera: result.camera as PermState,
          location: result.location as PermState,
          notifications: result.notifications as PermState,
        });
      }
    } else {
      const status = await requestAllWeb();
      setPermissions(status);
    }
  }, []);

  // On web (non-native), notifications are optional — browsers may permanently
  // block re-requesting them, and the app is primarily for Android devices.
  const allGranted =
    permissions.microphone === 'granted' &&
    permissions.camera === 'granted' &&
    permissions.location === 'granted' &&
    (isNative ? permissions.notifications === 'granted' : true);

  return {
    permissions,
    checking,
    allGranted,
    requestAll,
    refresh,
    hasRequested: hasRequestedRef.current,
  };
}
